import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatTag } from '@il-colpo/shared/account';
import { AvatarFigure } from '../../account/AvatarFigure.tsx';
import { useAccount } from '../../account/accountStore.ts';
import { clock, stars } from '../../game/format.ts';
import { useMetaClient, useRemote } from '../../meta/hooks.ts';
import type { TrainingBoardRow } from '../../meta/types.ts';
import { syncTrainingRecords } from '../../meta/trainingSync.ts';
import { Panel } from '../../ui/index.ts';
import metaStyles from '../meta/Meta.module.css';

/**
 * The fastest times of a puzzle at a level (GDD §11). It is for accounts: a guest and somebody who is
 * signed out are told what to do, and a build with no account service has no board at all. The
 * times are told by the apps and cannot be proved, which the hint says.
 */
export function TrainingBoard({ puzzle, level }: { puzzle: string; level: number }) {
  const { t } = useTranslation('training');
  const available = useAccount((s) => s.setup.available);
  const snapshot = useAccount((s) => s.snapshot);
  const meta = useMetaClient();
  const [told, setTold] = useState(false);
  const signedIn = snapshot.status === 'signedIn' && !snapshot.needsSecondFactor;
  const guest = signedIn && (snapshot.user?.anonymous ?? false);
  const eligible = meta !== null && signedIn && !guest;

  // The records of this device go up before the board is read, so that the player's own are on it.
  useEffect(() => {
    if (!eligible) return;
    let alive = true;
    void syncTrainingRecords().finally(() => {
      if (alive) setTold(true);
    });
    return () => {
      alive = false;
    };
  }, [eligible]);

  const load = useMemo(
    () => (meta && eligible && told ? () => meta.trainingBoard(puzzle, level, 10) : null),
    [meta, eligible, told, puzzle, level],
  );
  const board = useRemote<TrainingBoardRow[]>(load);

  if (!available) return null;

  return (
    <Panel tone="blueprint" title={t('board.title')}>
      <div className={metaStyles['stack']}>
        {snapshot.status !== 'signedIn' ? (
          <p className={metaStyles['note']}>{t('board.signIn')}</p>
        ) : null}
        {guest ? <p className={metaStyles['note']}>{t('board.guest')}</p> : null}
        {eligible ? (
          <>
            {board.status === 'error' ? (
              <p className={metaStyles['error']} role="alert">
                {t('board.failed')}
              </p>
            ) : null}
            {board.status === 'ready' && (board.data?.length ?? 0) === 0 ? (
              <p className={metaStyles['note']}>{t('board.empty')}</p>
            ) : null}
            {board.data && board.data.length > 0 ? (
              <ol className={metaStyles['board']} data-testid="training-board">
                {board.data.map((row) => (
                  <li key={row.position} className={metaStyles['boardRow']} data-me={row.isMe}>
                    <span className={metaStyles['position']}>{row.position}</span>
                    <AvatarFigure avatar={row.avatar} size="2.2rem" />
                    <span className={metaStyles['who']}>
                      <strong>{`${row.nickname}#${formatTag(row.tag)}`}</strong>
                      <span className={metaStyles['note']}>
                        <span aria-label={t('starsLabel', { count: row.stars })}>
                          {stars(row.stars)}
                        </span>
                        {row.isMe ? ` · ${t('board.you')}` : ''}
                      </span>
                    </span>
                    <span className={metaStyles['score']}>{clock(row.bestMs)}</span>
                  </li>
                ))}
              </ol>
            ) : null}
            <p className={metaStyles['note']}>{t('board.hint')}</p>
          </>
        ) : null}
      </div>
    </Panel>
  );
}
